import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, fmtCRC, fmtDate, dueLabel, daysUntil, sinceLabel, waLink } from '../api';
import { useAuth } from '../AuthContext';
import { useWhatsAppChannel } from '../hooks/useWhatsAppChannel';
import Modal from '../components/Modal';
import SubscriptionDueDateField from '../components/SubscriptionDueDateField';
import { IconChat, IconCheck, IconSend, IconX } from '../components/Icons';

const empty = { name: '', phone: '', email: '', notes: '' };

// Filtros por estado de cobro del cliente (los resuelve /api/clients?due=)
const DUE_TABS = [
  { key: '', label: 'Todos' },
  { key: 'vencidas', label: 'Ya le venció' },
  { key: 'por_vencer', label: 'Por vencer' },
  { key: 'atencion', label: 'Necesitan atención' },
];
const DUE_WINDOWS = [3, 7, 15, 30];

const statusBadge = {
  activa: 'bg-emerald-500/20 text-emerald-300',
  vencida: 'bg-red-500/20 text-red-300',
  cancelada: 'bg-slate-500/20 text-slate-400',
};

export default function Clients() {
  const { user } = useAuth();
  const { isPro, connected, instant } = useWhatsAppChannel();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState('');
  const [due, setDue] = useState('');
  const [days, setDays] = useState(7);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [error, setError] = useState('');
  const [editingSubscriptions, setEditingSubscriptions] = useState(null);
  const [subscriptionsError, setSubscriptionsError] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState({});
  const [sending, setSending] = useState('');
  const [sent, setSent] = useState({});
  const [waNotice, setWaNotice] = useState({ text: '', ok: true });

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (due) {
      params.set('due', due);
      params.set('days', String(days));
    }
    const qs = params.toString();
    return api(`/clients${qs ? `?${qs}` : ''}`).then(setClients).catch(() => {});
  };
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q, due, days]);

  useEffect(() => {
    if (!modal || !editing) return;
    let current = true;
    setEditingSubscriptions(null);
    setSubscriptionsError('');
    api(`/clients/${editing._id}`).then((client) => {
      if (current) setEditingSubscriptions(client.subscriptions || []);
    }).catch((err) => {
      if (current) setSubscriptionsError(err.message);
    });
    return () => { current = false; };
  }, [modal, editing?._id]);

  const onDateSaved = (updated) => {
    setEditingSubscriptions((items) => items?.map((s) => s._id === updated._id ? updated : s) ?? null);
    setDetails((items) => {
      const cached = items[editing._id];
      if (!cached) return items;
      return { ...items, [editing._id]: { ...cached, subscriptions: cached.subscriptions.map((s) => s._id === updated._id ? updated : s) } };
    });
    load();
  };

  // Auto-cierra el aviso flotante (pochoque) después de unos segundos
  useEffect(() => {
    if (!waNotice.text) return undefined;
    const t = setTimeout(() => setWaNotice({ text: '', ok: true }), 4500);
    return () => clearTimeout(t);
  }, [waNotice]);

  const toggleExpand = async (c) => {
    if (expanded === c._id) {
      setExpanded(null);
      return;
    }
    setExpanded(c._id);
    if (!details[c._id]) {
      try {
        const d = await api(`/clients/${c._id}`);
        setDetails((m) => ({ ...m, [c._id]: d }));
      } catch {
        /* ignora */
      }
    }
  };

  const open = (client) => {
    setEditing(client || null);
    setForm(client ? { name: client.name, phone: client.phone, email: client.email, notes: client.notes } : empty);
    setError('');
    setModal(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api(`/clients/${editing._id}`, { method: 'PUT', body: form });
      else await api('/clients', { method: 'POST', body: form });
      setModal(false);
      setDetails((m) => ({ ...m, [editing?._id]: undefined }));
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (c) => {
    if (!confirm(`¿Eliminar a ${c.name} y todas sus suscripciones?`)) return;
    await api(`/clients/${c._id}`, { method: 'DELETE' });
    load();
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const svcName = (s) => (s.kind === 'combo' ? s.comboId?.name : s.platformId?.name) || '—';

  const wa = user?.whatsapp || {};

  // Misma plantilla que usaría el envío automático: vencida → "pago vencido".
  const waFor = (client, s) => {
    const overdue = daysUntil(s.nextDueDate) < 0;
    const template = overdue ? wa.overdueTemplate : (wa.reminderTemplate || user?.waTemplate);
    return waLink(client.phone, template, {
      cliente: client.name,
      servicio: svcName(s),
      fecha: fmtDate(s.nextDueDate),
      monto: fmtCRC(s.price),
      correo: s.accountEmail || 'No aplica',
      contrasena: s.accountPassword || 'No aplica',
      perfil: s.profileName || 'No aplica',
      pin: s.pin || 'No aplica',
      negocio: user?.businessName || user?.name,
    }, wa.countryCode);
  };

  const renewLink = (client) =>
    waLink(client.phone, wa.renewalTemplate, {
      cliente: client.name,
      servicio: client.nextRenewal?.servicio || '',
      fecha: client.nextRenewal ? fmtDate(client.nextRenewal.fecha) : '',
      monto: client.nextRenewal ? fmtCRC(client.nextRenewal.monto) : '',
      negocio: user?.businessName || user?.name,
    }, wa.countryCode);

  // Plan Pro/Premium: envío automático por el número conectado (sin abrir WhatsApp Web)
  const sendRenewal = async (client) => {
    const subId = client.nextRenewal?.subscriptionId;
    if (!subId) {
      setWaNotice({ text: `${client.name} no tiene una suscripción activa para renovar.`, ok: false });
      return;
    }
    const key = `renew:${client._id}`;
    setSending(key);
    setWaNotice({ text: '', ok: true });
    try {
      await api(`/whatsapp/subscriptions/${subId}/renewal`, { method: 'POST' });
      setSent((current) => ({ ...current, [key]: true }));
      setWaNotice({ text: `Mensaje de renovación enviado a ${client.name}.`, ok: true });
    } catch (error) {
      setWaNotice({ text: error.message, ok: false });
    } finally {
      setSending('');
    }
  };

  const sendReminder = async (client, subscription) => {
    setSending(subscription._id);
    setWaNotice({ text: '', ok: true });
    try {
      await api(`/whatsapp/subscriptions/${subscription._id}/reminder`, {
        method: 'POST',
        body: { type: daysUntil(subscription.nextDueDate) < 0 ? 'overdue' : 'reminder' },
      });
      setSent((current) => ({ ...current, [subscription._id]: true }));
      setWaNotice({ text: `Mensaje enviado a ${client.name}.`, ok: true });
    } catch (error) {
      setWaNotice({ text: error.message, ok: false });
    } finally {
      setSending('');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">Clientes</h1>
        <div className="flex items-center gap-2">
          <button className="btn-primary" onClick={() => open()}>+ Nuevo cliente</button>
        </div>
      </div>

      <input className="input max-w-sm" placeholder="Buscar por nombre o teléfono…" value={q} onChange={(e) => setQ(e.target.value)} />

      {/* Filtro por vencimiento: quiénes ya vencieron y quiénes están por vencer */}
      <div className="due-filter-row">
        {DUE_TABS.map((t) => (
          <button
            key={t.key || 'todos'}
            type="button"
            onClick={() => setDue(t.key)}
            className={`due-chip ${t.key === 'vencidas' ? 'is-danger' : t.key ? 'is-amber' : 'is-neutral'} ${due === t.key ? 'is-active' : ''}`}
          >
            {t.label}
          </button>
        ))}
        {due && due !== 'vencidas' && (
          <select className="input inline-block w-auto" value={days} onChange={(e) => setDays(Number(e.target.value))}>
            {DUE_WINDOWS.map((d) => <option key={d} value={d}>próximos {d} días</option>)}
          </select>
        )}
      </div>
      {isPro && !connected && (
        <div className="reminder-plan-notice is-basic">
          <span><IconChat size={18} /></span>
          <div>
            <strong>WhatsApp Pro sin vincular</strong>
            <p>Mientras conectas tu número, los botones abren WhatsApp con el mensaje listo para que lo confirmes.</p>
          </div>
          <a href="/ajustes">Vincular</a>
        </div>
      )}
      {waNotice.text && (
        <div className={`wa-floating-notice ${waNotice.ok ? 'is-success' : 'is-error'}`}>
          {waNotice.ok ? <IconCheck size={16} /> : <IconX size={16} />}{waNotice.text}
        </div>
      )}

      <div className="card overflow-x-auto p-0">
        <table className="w-full min-w-[560px] text-sm">
          <thead>
            <tr className="border-b border-slate-800 text-left text-slate-400">
              <th className="p-3 w-8"></th>
              <th className="p-3">Nombre</th>
              <th className="p-3">WhatsApp</th>
              <th className="p-3">Activas</th>
              <th className="p-3">Vencimiento</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => {
              const det = details[c._id];
              const isOpen = expanded === c._id;
              return (
                <Fragment key={c._id}>
                  <tr className="cursor-pointer border-b border-slate-800/50 hover:bg-slate-800/30" onClick={() => toggleExpand(c)}>
                    <td className="p-3 text-slate-500">{isOpen ? '▾' : '▸'}</td>
                    <td className="p-3 font-medium">{c.name}</td>
                    <td className="p-3 text-slate-400">{c.phone}</td>
                    <td className="p-3"><span className="badge bg-indigo-500/20 text-indigo-300">{c.activeSubs}</span></td>
                    <td className="p-3">
                      {c.expiredSubs > 0 && (
                        <span className="badge bg-red-500/20 text-red-300" title={`Debe ${fmtCRC(c.overdueAmount)}`}>
                          {c.expiredSubs} vencida{c.expiredSubs !== 1 && 's'}
                        </span>
                      )}
                      {c.dueSoonSubs > 0 && (
                        <span className={`badge bg-amber-500/20 text-amber-300 ${c.expiredSubs > 0 ? 'ml-1' : ''}`}>
                          {c.dueSoonSubs} por vencer
                        </span>
                      )}
                      {!c.expiredSubs && !c.dueSoonSubs && (
                        <span className="text-xs text-slate-500">
                          {c.nextDueDate ? dueLabel(c.nextDueDate) : '—'}
                        </span>
                      )}
                    </td>
                    <td className="p-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {instant ? (
                        <button
                          type="button"
                          onClick={() => sendRenewal(c)}
                          disabled={!c.nextRenewal || sending === `renew:${c._id}` || sent[`renew:${c._id}`]}
                          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-50"
                          title={c.nextRenewal ? 'Enviar renovación automática por WhatsApp' : 'Sin suscripción activa para renovar'}
                        >
                          {sent[`renew:${c._id}`] ? <IconCheck size={14} /> : <IconSend size={14} />}
                          {sent[`renew:${c._id}`] ? 'Enviado' : sending === `renew:${c._id}` ? 'Enviando…' : 'Renovar'}
                        </button>
                      ) : (
                        <a
                          href={renewLink(c)}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-emerald-500"
                          title="Enviar mensaje de renovación por WhatsApp"
                        >
                          <IconChat size={14} /> Renovar
                        </a>
                      )}
                      <button className="ml-3 text-slate-400 hover:text-indigo-300" onClick={() => open(c)}>Editar</button>
                      <Link className="ml-3 text-indigo-400 hover:text-indigo-300" to={`/suscripciones?clientId=${c._id}`}>Suscripciones</Link>
                      <button className="ml-3 text-slate-400 hover:text-red-400" onClick={() => remove(c)}>Eliminar</button>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="border-b border-slate-800/50 bg-slate-900/40">
                      <td></td>
                      <td colSpan="5" className="p-3">
                        {!det ? (
                          <p className="text-slate-500">Cargando…</p>
                        ) : (
                          <div className="space-y-3">
                            <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-400">
                              <span>Cliente desde: <span className="text-slate-200">{fmtDate(det.createdAt)}</span> ({sinceLabel(det.createdAt)})</span>
                              {det.email && <span>Email: <span className="text-slate-200">{det.email}</span></span>}
                              {det.notes && <span>Notas: <span className="text-slate-200">{det.notes}</span></span>}
                            </div>

                            {det.subscriptions?.length === 0 && <p className="text-slate-500">Sin suscripciones.</p>}
                            <div className="grid gap-2 md:grid-cols-2">
                              {det.subscriptions?.map((s) => {
                                const d = daysUntil(s.nextDueDate);
                                const dColor = d == null ? 'text-slate-500' : d < 0 ? 'text-red-400' : d <= 3 ? 'text-amber-400' : 'text-slate-400';
                                return (
                                  <div key={s._id} className="rounded-lg border border-slate-800 bg-slate-900 p-3">
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="font-medium">
                                        {svcName(s)} <span className="text-xs text-slate-500">({s.unit === 'cuenta' ? 'cuenta' : 'perfil'})</span>
                                      </div>
                                      <span className={`badge ${statusBadge[s.status]}`}>{s.status}</span>
                                    </div>
                                    <div className="mt-1 text-sm text-slate-400">
                                      {fmtCRC(s.price)}/mes · vence {fmtDate(s.nextDueDate)}
                                      {s.status !== 'cancelada' && <span className={`ml-1 ${dColor}`}>({dueLabel(s.nextDueDate)})</span>}
                                    </div>
                                    {(s.accountEmail || s.profileName || s.pin) && (
                                      <div className="mt-2 space-y-0.5 rounded bg-slate-800/60 p-2 text-xs text-slate-300">
                                        {s.accountEmail && <div>Correo: {s.accountEmail}</div>}
                                        {s.accountPassword && <div>Contraseña: {s.accountPassword}</div>}
                                        {s.profileName && <div>Perfil: {s.profileName}</div>}
                                        {s.pin && <div>PIN: {s.pin}</div>}
                                      </div>
                                    )}
                                    {s.status !== 'cancelada' && (instant ? (
                                      <button type="button" onClick={() => sendReminder(c, s)} disabled={sending === s._id || sent[s._id]} className="btn mt-2 bg-emerald-600 text-white hover:bg-emerald-500">
                                        {sent[s._id] ? <IconCheck size={16} /> : <IconSend size={16} />}
                                        {sent[s._id] ? 'Enviado' : sending === s._id ? 'Enviando…' : 'Enviar recordatorio'}
                                      </button>
                                    ) : (
                                      <a href={waFor(c, s)} target="_blank" rel="noreferrer" className="btn mt-2 bg-emerald-600 text-white hover:bg-emerald-500">
                                        <IconChat size={16} /> Abrir en WhatsApp
                                      </a>
                                    ))}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {clients.length === 0 && (
              <tr>
                <td colSpan="6" className="p-6 text-center text-slate-500">
                  {due
                    ? 'Ningún cliente coincide con este filtro de vencimiento.'
                    : 'No hay clientes todavía. ¡Agrega el primero!'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={modal} title={editing ? 'Editar cliente' : 'Nuevo cliente'} onClose={() => setModal(false)}>
        <form onSubmit={save} className="space-y-3">
          <input className="input" placeholder="Nombre completo" value={form.name} onChange={set('name')} required />
          <input className="input" placeholder="WhatsApp (ej: 88881234)" value={form.phone} onChange={set('phone')} required />
          <input className="input" type="email" placeholder="Email (opcional)" value={form.email} onChange={set('email')} />
          <textarea className="input" rows="2" placeholder="Notas (opcional)" value={form.notes} onChange={set('notes')} />
          {editing && (
            <div className="space-y-3 border-t border-slate-800 pt-3">
              <h3 className="font-semibold">Fechas de renovación</h3>
              <p className="text-xs text-slate-400">Guarda cada fecha con su botón. No se registra un pago ni se elimina la suscripción.</p>
              {subscriptionsError ? (
                <p className="text-sm text-red-400" role="alert">No se pudieron cargar las suscripciones: {subscriptionsError}</p>
              ) : editingSubscriptions === null ? (
                <p className="text-sm text-slate-400">Cargando suscripciones…</p>
              ) : editingSubscriptions.filter((s) => s.status !== 'cancelada').length === 0 ? (
                <p className="text-sm text-slate-400">Este cliente no tiene suscripciones activas o vencidas.</p>
              ) : editingSubscriptions.filter((s) => s.status !== 'cancelada').map((s) => (
                <SubscriptionDueDateField key={s._id} subscription={s} onSaved={onDateSaved} />
              ))}
              <Link className="inline-block text-sm text-indigo-400 hover:text-indigo-300" to={`/suscripciones?clientId=${editing._id}`}>Ver todas sus suscripciones</Link>
            </div>
          )}
          {error && <p className="text-sm text-red-400">{error}</p>}
          <button className="btn-primary w-full">{editing ? 'Guardar cambios' : 'Crear cliente'}</button>
        </form>
      </Modal>
    </div>
  );
}
